import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  MagnifyingGlassIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

const RegisterTypeSelectPage = ({ userType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Removed unused handleSocialLogin logic since buttons were moved

  const handleJobSeekerSelect = () => {
    const finalUserType = userType || 'job_seeker'; // userTypeが指定されていればそれを使用
    navigate(`/register?type=${finalUserType}`);
  };

  const handleEmployerSelect = () => {
    const finalUserType = userType || 'employer'; // userTypeが指定されていればそれを使用
    navigate(`/register?type=${finalUserType}`);
  };

  // ログイン済みユーザーの種別チェック
  const isJobSeeker = user && user.user_type === 'job_seeker';
  const isEmployer = user && user.user_type === 'employer';

  // 表示制御のロジック
  // 要望に合わせて、基本は両方のボタンを表示する
  // ただし、ログイン済みで片方の種別の場合は、矛盾する方を隠す
  const showJob = (() => {
    if (!user) return true;
    return !isEmployer; // 事業者でログイン中は求職者登録を非表示
  })();

  const showEmployer = (() => {
    if (!user) return true;
    return !isJobSeeker; // 求職者でログイン中は事業者登録を非表示
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            トップに戻る
          </button>
        </div>

        <div className="text-center">
          {/* Logo */}
          <div className="mx-auto h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center mb-4">
            <span className="text-white font-bold text-xl">B</span>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            {(showJob && showEmployer)
              ? 'アカウント作成'
              : (userType === 'job_seeker' ? '求職者アカウント作成' : '事業者アカウント作成')
            }
          </h2>
          <p className="text-sm text-gray-600">
            {userType ?
              (userType === 'job_seeker' ?
                'アルバイト・パート求人を探してお仕事にエントリー' :
                '即日バイト求人を投稿してスタッフを募集'
              ) :
              'ご利用目的に合わせてアカウントタイプをお選びください'
            }
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-xl sm:rounded-lg sm:px-10">

            {/* Social Buttons REMOVED - Moved to specific registration pages */}

            <div className="text-center mb-6">
              <p className="text-gray-500 text-sm">まずはじめに、ご希望の登録タイプを選択してください。</p>
            </div>

            {/* 求職者登録ボタン */}
            {showJob && (
              <div className="mb-6">
                <button
                  onClick={handleJobSeekerSelect}
                  className="w-full group relative flex flex-col items-center justify-center p-6 border-2 border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 group-hover:bg-blue-200 transition-colors">
                    <MagnifyingGlassIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    求職者として登録する
                  </h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    履歴書不要で<br />
                    今すぐお仕事エントリー
                  </p>
                  <div className="mt-4 flex items-center text-sm text-blue-600">
                    <span>アルバイト・パートを探す</span>
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>
            )}

            {/* 事業者登録ボタン */}
            {showEmployer && (
              <div className="mb-6">
                <button
                  onClick={handleEmployerSelect}
                  className="w-full group relative flex flex-col items-center justify-center p-6 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 group-hover:bg-green-200 transition-colors">
                    <BriefcaseIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    事業者として登録する
                  </h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    最短3分で求人掲載<br />
                    急な人手不足も解決
                  </p>
                  <div className="mt-4 flex items-center text-sm text-green-600">
                    <span>求人を投稿・スタッフ採用</span>
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>
            )}

            {/* ユーザー種別が指定されている場合の説明メッセージ */}
            {userType && !(showJob && showEmployer) && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 text-center">
                  {userType === 'job_seeker' ?
                    '求職者アカウントを作成します' :
                    '事業者アカウントを作成します'
                  }
                </p>
              </div>
            )}

            {/* ログインしているユーザーに対する説明メッセージ（共通ページのみ） */}
            {!userType && (isJobSeeker || isEmployer) && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  {isJobSeeker && '求職者としてログイン中のため、事業者登録は利用できません。'}
                  {isEmployer && '事業者としてログイン中のため、求職者登録は利用できません。'}
                </p>
              </div>
            )}

            {/* ログインリンク（共通ページまたは何もボタンが表示されない場合のみ） */}
            {(!userType || (!showJob && !showEmployer)) && (
              <div className="mt-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">
                      すでにアカウントをお持ちですか？
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                  >
                    ログイン
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterTypeSelectPage;